import { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  RecurrencePattern,
  TaskRecurrence,
  MonthlyMode,
  DAYS_OF_WEEK,
  WEEKS_OF_MONTH,
  MONTHS_OF_YEAR,
  defaultsForPattern,
  validateRecurrence,
} from '../types/recurrence';
import { useOutsideClick } from '../hooks/useOutsideClick';

// Compact multi-select dropdown used by the Monthly "On the" picker.
// Modelled on RolesDropdown in TasksPage.tsx — same closed-state behaviour
// (button-like trigger with placeholder when nothing selected; comma-joined
// label preview when items are selected), checkbox panel, outside-click to
// dismiss. Local to this file because it's only used here.
interface MultiSelectOption<T extends number | string> {
  value: T;
  label: string;
  short?: string;  // Optional shorter label for the closed-state preview
}
interface MultiSelectDropdownProps<T extends number | string> {
  options: MultiSelectOption<T>[];
  selected: T[];
  onChange: (next: T[]) => void;
  placeholder: string;
  disabled?: boolean;
}
function MultiSelectDropdown<T extends number | string>({
  options, selected, onChange, placeholder, disabled,
}: MultiSelectDropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useOutsideClick(ref, () => setOpen(false), open);

  const previewLabel = selected.length === 0
    ? placeholder
    : options
        .filter((o) => selected.includes(o.value))
        .map((o) => o.short ?? o.label)
        .join(', ');

  const toggle = (v: T) => {
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  };

  return (
    <div className={`relative ${disabled ? 'opacity-40 pointer-events-none' : ''}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
      >
        <span className={`truncate ${selected.length === 0 ? 'text-gray-500' : 'text-gray-900'}`}>
          {previewLabel}
        </span>
        <ChevronDown className={`w-4 h-4 flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {options.map((opt) => {
            const checked = selected.includes(opt.value);
            return (
              <label
                key={String(opt.value)}
                className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(opt.value)}
                  className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                />
                <span className="text-gray-700">{opt.label}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface RecurrenceSelectorProps {
  value: Partial<TaskRecurrence>;
  onChange: (recurrence: Partial<TaskRecurrence>) => void;
  startDate?: string;
  startDateSlot?: React.ReactNode;
  // 'buttons' (default, backwards-compatible) renders pattern as a row of 5
  // buttons with fields stacked. 'compact' uses a <select> for the pattern
  // and lays Start Date / Interval / End into a single row.
  variant?: 'buttons' | 'compact';
  // When true, hides the top-level pattern picker (the dropdown that
  // chooses Daily / Weekly / Monthly). Caller is responsible for rendering
  // its own pattern control elsewhere in the form — typically used by
  // TasksPage to place the Recurrence dropdown on the same row as
  // Assign to Roles, with the pattern-specific details (Repeat On chips /
  // Monthly modes / Start Date row) flowing below the Completion Scope
  // section instead of immediately under the picker.
  hidePatternPicker?: boolean;
}

export default function RecurrenceSelector({ value, onChange, startDate, startDateSlot, variant = 'buttons', hidePatternPicker = false }: RecurrenceSelectorProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const detailsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value.recurrence_pattern && value.recurrence_pattern !== 'none') {
      setIsExpanded(true);
    }
  }, [value.recurrence_pattern]);

  const handlePatternChange = (pattern: RecurrencePattern) => {
    // Centralised defaults — both this component and TasksPage's external
    // pattern picker share the same write-back shape via defaultsForPattern.
    const newRecurrence = defaultsForPattern(pattern, startDate);

    if (pattern === 'none') {
      setIsExpanded(false);
    } else {
      setIsExpanded(true);
      // Scroll to details section after a brief delay to allow expansion
      setTimeout(() => {
        detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    }

    onChange(newRecurrence);
  };

  const updateRecurrence = (updates: Partial<TaskRecurrence>) => {
    const validationError = validateRecurrence({ ...value, ...updates });
    if (validationError) {
      setError(validationError);
    } else {
      setError(null);
    }
    onChange({ ...value, ...updates });
  };

  const toggleDayOfWeek = (day: number) => {
    const currentDays = value.recurrence_days_of_week || [];
    const newDays = currentDays.includes(day)
      ? currentDays.filter(d => d !== day)
      : [...currentDays, day].sort();
    updateRecurrence({ recurrence_days_of_week: newDays });
  };

  const isCompact = variant === 'compact';

  return (
    <div className="space-y-3">
      {/* Pattern Selection — hidden when the caller wants to render its own
          pattern dropdown (e.g. TasksPage puts it inline with Assign to
          Roles). When hidden, this component only renders the
          pattern-specific details below. */}
      {!hidePatternPicker && (isCompact ? (
        <select
          value={value.recurrence_pattern || 'none'}
          onChange={(e) => handlePatternChange(e.target.value as RecurrencePattern)}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
        >
          {(['none', 'daily', 'weekly', 'monthly'] as RecurrencePattern[]).map(pattern => (
            <option key={pattern} value={pattern}>
              {pattern.charAt(0).toUpperCase() + pattern.slice(1)}
            </option>
          ))}
        </select>
      ) : (
        <div>
          <div className="grid grid-cols-4 gap-2">
            {(['none', 'daily', 'weekly', 'monthly'] as RecurrencePattern[]).map(pattern => (
              <button
                key={pattern}
                type="button"
                onClick={() => handlePatternChange(pattern)}
                className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                  value.recurrence_pattern === pattern
                    ? 'bg-primary-500 text-white border-primary-500'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {pattern.charAt(0).toUpperCase() + pattern.slice(1)}
              </button>
            ))}
          </div>
        </div>
      ))}

      {/* Start Date slot (buttons variant only — compact puts it in the 3-col row below) */}
      {!isCompact && isExpanded && value.recurrence_pattern !== 'none' && startDateSlot}

      {/* Pattern-specific options */}
      {isExpanded && value.recurrence_pattern !== 'none' && (
        <div ref={detailsRef} className="space-y-4 pt-2">
          {/* Interval — buttons variant only; compact puts this in the 3-col row below */}
          {!isCompact && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Repeat Every
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  max="99"
                  value={value.recurrence_interval || 1}
                  onChange={(e) => updateRecurrence({ recurrence_interval: parseInt(e.target.value) || 1 })}
                  className="w-20 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-600">
                  {value.recurrence_pattern === 'daily' && 'day(s)'}
                  {value.recurrence_pattern === 'weekly' && 'week(s)'}
                  {value.recurrence_pattern === 'monthly' && 'month(s)'}
                  {value.recurrence_pattern === 'yearly' && 'year(s)'}
                </span>
              </div>
            </div>
          )}

          {/* Weekly: Days of Week */}
          {value.recurrence_pattern === 'weekly' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Repeat On
              </label>
              <div className="flex gap-2">
                {DAYS_OF_WEEK.map(day => (
                  <button
                    key={day.value}
                    type="button"
                    onClick={() => toggleDayOfWeek(day.value)}
                    className={`flex-1 px-2 py-2 text-xs font-medium rounded-lg border transition-colors ${
                      value.recurrence_days_of_week?.includes(day.value)
                        ? 'bg-primary-500 text-white border-primary-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {day.short}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Monthly: three mutually-exclusive sub-modes (post 2026-05-22).
              Replaces the old On-day / On-the-Nth-weekday pair which
              couldn't express "1st + 3rd Monday" or "days 10, 15, 20" or
              "before the 25th". See plan for full rationale.

                day_range     → visibility from F through U of the month
                specific_days → 1–31 grid; visible only on selected dates
                nth_weekdays  → multi-ordinal × multi-weekday intersection
          */}
          {value.recurrence_pattern === 'monthly' && (() => {
            const setMode = (mode: MonthlyMode, patch: Partial<TaskRecurrence> = {}) => {
              // Switching mode wipes the inactive payload columns so the DB
              // never carries shadow values from a previous mode.
              updateRecurrence({
                recurrence_monthly_mode: mode,
                recurrence_month_range_from: mode === 'day_range' ? (patch.recurrence_month_range_from ?? null) : null,
                recurrence_month_range_until: mode === 'day_range' ? (patch.recurrence_month_range_until ?? null) : null,
                recurrence_specific_days_of_month: mode === 'specific_days' ? (patch.recurrence_specific_days_of_month ?? []) : null,
                recurrence_nth_weekdays: mode === 'nth_weekdays'
                  ? (patch.recurrence_nth_weekdays ?? { ordinals: [], weekdays: [] })
                  : null,
                // Legacy single-value fields stay cleared once on the new model.
                recurrence_day_of_month: undefined,
                recurrence_week_of_month: undefined,
              });
            };
            const mode: MonthlyMode = (value.recurrence_monthly_mode as MonthlyMode) || 'day_range';

            return (
              <div className="space-y-4">
                {/* ── Mode 1: Day range ─────────────────────────────────── */}
                <div>
                  <label className="flex items-center gap-2 mb-2">
                    <input
                      type="radio"
                      name="monthly-mode"
                      checked={mode === 'day_range'}
                      onChange={() => setMode('day_range')}
                      className="w-4 h-4 text-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Day range</span>
                  </label>
                  <div className="flex items-center gap-2 ml-6">
                    <span className="text-xs text-gray-500">From</span>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={value.recurrence_month_range_from ?? ''}
                      onChange={(e) => {
                        const n = parseInt(e.target.value);
                        setMode('day_range', { recurrence_month_range_from: Number.isFinite(n) ? n : null, recurrence_month_range_until: value.recurrence_month_range_until ?? null });
                      }}
                      disabled={mode !== 'day_range'}
                      className="w-16 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                    />
                    <span className="text-xs text-gray-500">Until</span>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={value.recurrence_month_range_until ?? ''}
                      onChange={(e) => {
                        const n = parseInt(e.target.value);
                        setMode('day_range', { recurrence_month_range_from: value.recurrence_month_range_from ?? null, recurrence_month_range_until: Number.isFinite(n) ? n : null });
                      }}
                      disabled={mode !== 'day_range'}
                      className="w-16 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                    />
                    <span className="text-xs text-gray-500">of the month</span>
                  </div>
                  <p className="ml-6 mt-1 text-[11px] text-gray-500">
                    Visible from From through Until (inclusive). Leave a side blank for an open-ended range.
                  </p>
                </div>

                {/* ── Mode 2: Specific days (1–31 grid) ─────────────────── */}
                <div>
                  <label className="flex items-center gap-2 mb-2">
                    <input
                      type="radio"
                      name="monthly-mode"
                      checked={mode === 'specific_days'}
                      onChange={() => setMode('specific_days')}
                      className="w-4 h-4 text-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Specific days</span>
                  </label>
                  <div className={`ml-6 grid grid-cols-7 gap-1.5 ${mode !== 'specific_days' ? 'opacity-40 pointer-events-none' : ''}`}>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => {
                      const selected = Array.isArray(value.recurrence_specific_days_of_month)
                        && value.recurrence_specific_days_of_month.includes(d);
                      return (
                        <button
                          key={d}
                          type="button"
                          onClick={() => {
                            const current = Array.isArray(value.recurrence_specific_days_of_month)
                              ? value.recurrence_specific_days_of_month
                              : [];
                            const next = current.includes(d)
                              ? current.filter((x) => x !== d)
                              : [...current, d].sort((a, b) => a - b);
                            setMode('specific_days', { recurrence_specific_days_of_month: next });
                          }}
                          disabled={mode !== 'specific_days'}
                          className={`h-8 text-xs font-medium rounded border transition-colors ${
                            selected
                              ? 'bg-primary-500 text-white border-primary-500'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          } disabled:cursor-not-allowed`}
                        >
                          {d}
                        </button>
                      );
                    })}
                  </div>
                  <p className="ml-6 mt-1 text-[11px] text-gray-500">
                    Task visible only on the selected dates each month.
                  </p>
                </div>

                {/* ── Mode 3: Nth weekday(s) ─────────────────────────────────
                    Two multi-select dropdowns — ordinals on the left,
                    weekdays on the right. Closed state shows a comma-joined
                    preview ("First, Third" / "Mon, Wed"); opening reveals a
                    checkbox list. The cross-product is computed at runtime
                    in isTaskScheduledForDate. */}
                <div>
                  <label className="flex items-center gap-2 mb-2">
                    <input
                      type="radio"
                      name="monthly-mode"
                      checked={mode === 'nth_weekdays'}
                      onChange={() => setMode('nth_weekdays')}
                      className="w-4 h-4 text-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700">On the</span>
                  </label>
                  <div className="ml-6 grid grid-cols-2 gap-2">
                    <MultiSelectDropdown<number>
                      placeholder="Select ordinal(s)…"
                      disabled={mode !== 'nth_weekdays'}
                      options={WEEKS_OF_MONTH.map((w) => ({
                        // "Last" (UI value 5) is stored as -1 on the backend
                        // so isTaskScheduledForDate can recognise the
                        // last-occurrence intent without month-end math
                        // baked into the data.
                        value: w.value === 5 ? -1 : w.value,
                        label: w.label,
                      }))}
                      selected={value.recurrence_nth_weekdays?.ordinals ?? []}
                      onChange={(next) => {
                        const sel = value.recurrence_nth_weekdays || { ordinals: [], weekdays: [] };
                        setMode('nth_weekdays', {
                          recurrence_nth_weekdays: {
                            ordinals: [...next].sort((a, b) => a - b),
                            weekdays: sel.weekdays,
                          },
                        });
                      }}
                    />
                    <MultiSelectDropdown<number>
                      placeholder="Select weekday(s)…"
                      disabled={mode !== 'nth_weekdays'}
                      options={DAYS_OF_WEEK.map((d) => ({
                        value: d.value,
                        label: d.label,
                        short: d.short,
                      }))}
                      selected={value.recurrence_nth_weekdays?.weekdays ?? []}
                      onChange={(next) => {
                        const sel = value.recurrence_nth_weekdays || { ordinals: [], weekdays: [] };
                        setMode('nth_weekdays', {
                          recurrence_nth_weekdays: {
                            ordinals: sel.ordinals,
                            weekdays: [...next].sort((a, b) => a - b),
                          },
                        });
                      }}
                    />
                  </div>
                  <p className="ml-6 mt-1 text-[11px] text-gray-500">
                    Visible on every selected ordinal × weekday in the month. E.g. First + Third × Mon → 1st and 3rd Monday.
                  </p>
                </div>
              </div>
            );
          })()}

          {/* Yearly: Month and Day */}
          {value.recurrence_pattern === 'yearly' && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Month
                </label>
                <select
                  value={value.recurrence_month_of_year || ''}
                  onChange={(e) => updateRecurrence({ recurrence_month_of_year: parseInt(e.target.value) || undefined })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {MONTHS_OF_YEAR.map(month => (
                    <option key={month.value} value={month.value}>{month.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Day
                </label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={value.recurrence_day_of_month || ''}
                  onChange={(e) => updateRecurrence({ recurrence_day_of_month: parseInt(e.target.value) || undefined })}
                  className="w-20 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          )}

          {/* End Condition */}
          {isCompact ? (
            /* Compact: Start Date / Repeat Every / End Date in one row.
               Grid columns are dynamic — 3 when End Date is "Never", 4 when
               the admin picks "On date" / "After N times" so the extra
               input lands in-line as its own column instead of dropping
               into a cramped second row below the End Date cell. */
            (() => {
              const endType = value.recurrence_end_type || 'never';
              const hasExtra = endType === 'by_date' || endType === 'after_occurrences';
              const unitLabel =
                value.recurrence_pattern === 'daily' ? 'day(s)' :
                value.recurrence_pattern === 'weekly' ? 'week(s)' :
                value.recurrence_pattern === 'monthly' ? 'month(s)' :
                value.recurrence_pattern === 'yearly' ? 'year(s)' : '';
              return (
                <div className={`grid gap-3 items-start ${hasExtra ? 'grid-cols-4' : 'grid-cols-3'}`}>
                  {/* Start Date cell — reuse the caller's startDateSlot */}
                  <div className="min-w-0">
                    {startDateSlot}
                  </div>

                  {/* Repeat Every cell — unit ("week(s)", "day(s)", etc) is
                      pinned INSIDE the input as a right-side suffix so the
                      cell width stays tidy and matches the other columns
                      visually. */}
                  <div className="min-w-0">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Repeat Every
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min="1"
                        max="99"
                        value={value.recurrence_interval || 1}
                        onChange={(e) => updateRecurrence({ recurrence_interval: parseInt(e.target.value) || 1 })}
                        className="w-full h-10 pl-2 pr-14 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 select-none">
                        {unitLabel}
                      </span>
                    </div>
                  </div>

                  {/* End Date select — single cell, conditional sibling cell
                      below carries the extra input. h-10 forced on every
                      control in this row so native date / select / number
                      inputs render at the same height (their intrinsic
                      heights differ by a couple of pixels otherwise). */}
                  <div className="min-w-0">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Date
                    </label>
                    <select
                      value={endType}
                      onChange={(e) => {
                        const next = e.target.value as TaskRecurrence['recurrence_end_type'];
                        if (next === 'never') {
                          updateRecurrence({ recurrence_end_type: 'never', recurrence_end_date: undefined, recurrence_occurrences: undefined });
                        } else {
                          updateRecurrence({ recurrence_end_type: next });
                        }
                      }}
                      className="w-full h-10 px-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                    >
                      <option value="never">Never</option>
                      <option value="by_date">On date</option>
                      <option value="after_occurrences">After N times</option>
                    </select>
                  </div>

                  {/* 4th cell — only rendered when End Date isn't "Never".
                      Picks up the calendar (for "On date") or the count
                      input + "times" suffix (for "After N times"). */}
                  {hasExtra && (
                    <div className="min-w-0">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {endType === 'by_date' ? 'End On' : 'Occurrences'}
                      </label>
                      {endType === 'by_date' ? (
                        <input
                          type="date"
                          value={value.recurrence_end_date || ''}
                          onChange={(e) => updateRecurrence({ recurrence_end_date: e.target.value })}
                          className="w-full h-10 px-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      ) : (
                        <div className="relative">
                          <input
                            type="number"
                            min="1"
                            max="999"
                            value={value.recurrence_occurrences || ''}
                            onChange={(e) => updateRecurrence({ recurrence_occurrences: parseInt(e.target.value) || undefined })}
                            className="w-full h-10 pl-2 pr-12 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 select-none">
                            times
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="end-type"
                    checked={value.recurrence_end_type === 'never'}
                    onChange={() => updateRecurrence({ recurrence_end_type: 'never' })}
                    className="w-4 h-4 text-primary-500"
                  />
                  <span className="text-sm text-gray-700">Never</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="end-type"
                    checked={value.recurrence_end_type === 'by_date'}
                    onChange={() => updateRecurrence({ recurrence_end_type: 'by_date' })}
                    className="w-4 h-4 text-primary-500"
                  />
                  <span className="text-sm text-gray-700">On</span>
                  <input
                    type="date"
                    value={value.recurrence_end_date || ''}
                    onChange={(e) => updateRecurrence({
                      recurrence_end_type: 'by_date',
                      recurrence_end_date: e.target.value,
                    })}
                    disabled={value.recurrence_end_type !== 'by_date'}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                  />
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="end-type"
                    checked={value.recurrence_end_type === 'after_occurrences'}
                    onChange={() => updateRecurrence({ recurrence_end_type: 'after_occurrences' })}
                    className="w-4 h-4 text-primary-500"
                  />
                  <span className="text-sm text-gray-700">After</span>
                  <input
                    type="number"
                    min="1"
                    max="999"
                    value={value.recurrence_occurrences || ''}
                    onChange={(e) => updateRecurrence({
                      recurrence_end_type: 'after_occurrences',
                      recurrence_occurrences: parseInt(e.target.value) || undefined,
                    })}
                    disabled={value.recurrence_end_type !== 'after_occurrences'}
                    className="w-20 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                  />
                  <span className="text-sm text-gray-700">occurrence(s)</span>
                </label>
              </div>
            </div>
          )}

        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
    </div>
  );
}
